import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeading, CTASection } from "@/components/layout";
import { DevSafeForm } from "@/components/dev-form";
import { KIT_TAGS } from "@/lib/kit.functions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Who inside the agency is this work for?",
    a: "Engagements are built for the people who own community trust in practice: chiefs and sheriffs, command staff, Community Engagement supervisors and units, training leaders, and the officers carrying the work day to day. Most agencies bring a mix of leadership and practitioners into the same room, because the work only holds when both are aligned.",
  },
  {
    q: "How long does an engagement usually last?",
    a: "It depends on what the agency is trying to move. A keynote or workshop can be a single day. An assessment typically runs several weeks so there is time to gather honest internal and community input. Implementation support and unit development are longer relationships, often several months, because building trust infrastructure is not a one-session outcome.",
  },
  {
    q: "Can this be delivered virtually, or does it need to be onsite?",
    a: "Both. Training, advising, and planning sessions work well virtually. Assessments, facilitation of difficult conversations, and community-facing work are stronger onsite, where people can be in the same room and read each other honestly.",
  },
  {
    q: "How does cost and procurement work?",
    a: "Pricing is scoped to the engagement rather than sold as a fixed package, and we provide the documentation your agency needs for purchasing, grant funding, or training budget approval. Many agencies fund this work through existing professional development or community policing funds.",
  },
  {
    q: "How is impact measured?",
    a: "Through the Trust Evidence System. Instead of counting events and attendance, we document the commitments made, the behavior that followed, the outcomes observed, the limitations encountered, and the next decision required. That gives leadership something defensible to report and something real to act on.",
  },
  {
    q: "Does this replace our existing training?",
    a: "No. It complements it. Most agency training addresses tactics, law, and procedure. This work addresses trust conditions, engagement design, and the systems that carry relationships beyond one officer. It gives your existing training a foundation to connect to rather than competing with it.",
  },
];


const TITLE = "Community Engagement Training for Law Enforcement Agencies";
const DESC =
  "Tables and Bridges helps law enforcement agencies develop Community Engagement personnel, assess trust conditions, and build systems that last.";

export const Route = createFileRoute("/for-agencies")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/for-agencies" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/for-agencies" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),

  component: ForAgenciesPage,
});

const ENTRY_POINTS = [
  "Develop Our Officers",
  "Strengthen Our Community Engagement Strategy",
  "Assess a Trust Condition",
  "Build or Improve a Community Engagement Unit",
  "Facilitate a Difficult Conversation",
  "Improve How We Measure Impact",
  "Create an Implementation Plan",
];

const SUPPORT_TYPES = [
  "Keynote or conference session",
  "Workshop or training",
  "Trust or Community Engagement assessment",
  "Consulting",
  "Facilitation",
  "Implementation support",
  "Community Engagement Unit development",
  "Leadership advising",
  "Not sure yet",
];

function ForAgenciesPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-warm-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-25">
          <svg className="w-full h-full" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" fill="none">
            <defs>
              <pattern id="ag-grid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M80 0H0V80" stroke="#F7F5EF" strokeOpacity="0.3" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="1440" height="700" fill="url(#ag-grid)" />
            <path d="M60 540 L60 220 L720 220 L720 540 L1380 540 L1380 220" stroke="#F2B84B" strokeOpacity="0.55" />
          </svg>
        </div>
        <div className="container-editorial relative z-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <p className="eyebrow mb-8">For Law Enforcement Agencies</p>
          <h1 className="max-w-5xl font-display text-[clamp(2.5rem,5.6vw,5rem)] leading-[1] tracking-[-0.02em]">
            Move from Community Engagement activity to{" "}
            <span className="italic text-gold">trust infrastructure.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-warm-white/85 text-lg leading-relaxed">
            Tables and Bridges helps law enforcement agencies develop Community
            Engagement personnel, strengthen strategy, assess trust conditions, improve
            engagement design, and build systems that can carry relationships beyond
            one officer or one season.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <a href="#inquiry" className="btn-base btn-primary">Request an Agency Assessment</a>
          </div>
        </div>
      </section>

      {/* AGENCY CHALLENGE */}
      <Section tone="warm-white">
        <SectionHeading
          eyebrow="The agency challenge"
          title="Strong people cannot compensate for weak systems forever."
        />
        <ul className="mt-12 grid gap-3 md:grid-cols-2 max-w-5xl text-navy/85">
          {[
            "Unclear Community Engagement strategy",
            "Underdeveloped personnel",
            "Responsibility without enough authority",
            "Activity disconnected from outcomes",
            "Weak internal and external trust alignment",
            "Dependence on one trusted officer",
            "Difficulty documenting value",
            "Inconsistent leadership support",
            "Relationships that weaken during personnel transition",
          ].map((c) => (
            <li key={c} className="flex gap-3 border-t border-navy/10 pt-4">
              <span aria-hidden className="text-gold">▸</span>
              {c}
            </li>
          ))}
        </ul>
      </Section>

      {/* SERVICES */}
      <Section tone="ink">
        <SectionHeading
          eyebrow="Services"
          title="Align your agency with the national standard for Community Engagement."
          lede="Services are shaped by the agency's needs, authority, readiness, trust condition, and desired outcomes. Every engagement is built on the same methodology practitioners are trained in nationally, so the work your agency does connects to a shared standard rather than a one-off program."
        />

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[
            "Keynotes",
            "Workshops",
            "Agency Training",
            "Trust and Community Engagement Assessments",
            "Consulting",
            "Facilitation",
            "Implementation Support",
            "Community Engagement Unit Development",
            "Leadership Advising",
          ].map((s) => (
            <div key={s} className="border border-warm-white/12 p-6">
              <h3 className="font-display text-warm-white text-lg">{s}</h3>
            </div>
          ))}
        </div>
      </Section>

      {/* WHO WE WORK WITH */}
      <Section tone="warm-white">
        <SectionHeading eyebrow="Who we work with" title="Leaders and teams inside the agency." />
        <ul className="mt-10 grid gap-3 md:grid-cols-2 max-w-4xl text-navy/85">
          {[
            "Chiefs and sheriffs",
            "Command staff",
            "Community Engagement supervisors",
            "Community Engagement Units",
            "Training leaders",
            "Public information and outreach leaders",
            "Agency teams responsible for public trust and partnership",
          ].map((w) => (
            <li key={w} className="flex gap-3 border-t border-navy/10 pt-4">
              <span aria-hidden className="text-gold">▸</span>
              {w}
            </li>
          ))}
        </ul>
      </Section>

      {/* ENTRY POINTS */}
      <Section tone="deep-green">
        <SectionHeading eyebrow="Possible entry points" title="Where agencies typically begin." />
        <div className="mt-12 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {ENTRY_POINTS.map((e) => (
            <div key={e} className="border border-warm-white/15 bg-warm-white/5 p-5 text-warm-white">
              {e}
            </div>
          ))}
        </div>
      </Section>

      {/* PROCESS */}
      <Section tone="warm-white">
        <SectionHeading eyebrow="Agency process" title="A responsible path." />
        <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl">
          {[
            "Understand the condition",
            "Clarify the desired outcome",
            "Determine the appropriate service",
            "Design the engagement",
            "Implement responsibly",
            "Review evidence and next steps",
          ].map((step, i) => (
            <li key={step} className="border-l-2 border-gold pl-5 py-2">
              <span className="font-display text-gold text-3xl leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="mt-3 text-navy font-medium">{step}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* INQUIRY FORM */}
      <Section tone="navy" id="inquiry">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] items-start">
          <div>
            <p className="eyebrow mb-5">Agency inquiry</p>
            <h2 className="font-display">Start the conversation.</h2>
            <span aria-hidden className="divider-gold mt-6 mb-6" />
            <p className="text-warm-white/80 leading-relaxed">
              Share the condition, outcome, and timing. We will follow up to explore
              whether Tables and Bridges is the right partner.
            </p>
            <p className="mt-6 text-warm-white/60 text-xs leading-relaxed border-l border-gold/50 pl-4">
              Please do not include criminal justice information, active case details,
              confidential personnel information, protected investigative information,
              or sensitive internal documents.
            </p>
          </div>
          <div className="border border-warm-white/12 bg-warm-white/5 p-6 md:p-10">
            <DevSafeForm
              formName="agency-inquiry"
              kitTag={KIT_TAGS.agencyInquiry}
              tone="onNavy"
              submitLabel="Request an Agency Assessment"
              successMessage="Inquiry received. Our team will follow up within two business days."
              fields={[
                { name: "name", label: "Name", required: true },
                { name: "title", label: "Title", required: true },
                { name: "agency", label: "Agency", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone", type: "tel", optional: true },
                {
                  name: "size",
                  label: "Agency size",
                  type: "select",
                  optional: true,
                  options: [
                    "Fewer than 50 sworn",
                    "50 to 199 sworn",
                    "200 to 499 sworn",
                    "500 to 999 sworn",
                    "1000 or more sworn",
                  ],
                },
                {
                  name: "support",
                  label: "Type of support needed",
                  type: "select",
                  required: true,
                  full: true,
                  options: SUPPORT_TYPES,
                },
                {
                  name: "challenge",
                  label: "Primary challenge",
                  type: "textarea",
                  full: true,
                  rows: 4,
                },
                {
                  name: "timing",
                  label: "Desired timing",
                  type: "select",
                  options: [
                    "Within the next 30 days",
                    "1 to 3 months",
                    "3 to 6 months",
                    "6 to 12 months",
                    "Exploring only",
                  ],
                },
                { name: "notes", label: "Additional information", type: "textarea", full: true, rows: 3, optional: true },
              ]}
            />
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="warm-white" id="faq">
        <SectionHeading
          eyebrow="Agency questions"
          title="What agencies usually ask first."
        />
        <div className="mt-12 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={"faq-" + i} className="border-navy/15">
                <AccordionTrigger className="text-left font-display text-navy text-lg md:text-xl hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-navy/80 text-base leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>


      <CTASection
        title="Build a stronger system around the people doing the work."
        primary={{ label: "Request an Agency Assessment", href: "#inquiry" }}
        tone="deep-green"
      />
    </div>
  );
}
